import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ChatMessages from './ChatMessages';
import ChatInput from '../ChatInput';

function ChatThreadActionMenu({
  threadId,
  isArchived,
  onArchive,
  onRestore,
  onDelete,
  actionBusy,
  isSupportChat
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleAction = (action) => {
    setOpen(false);
    action?.(threadId);
  };

  return (
    <div ref={menuRef} className={`chat-thread-action-menu ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="chat-thread-action-menu__trigger"
        onClick={() => setOpen((current) => !current)}
        aria-label="Conversation actions"
        title="Conversation actions"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={actionBusy}
      >
        ...
      </button>
      {open && (
        <div
          className={`chat-thread-action-menu__panel ${isSupportChat ? 'chat-thread-action-menu__panel--support' : ''}`}
          role="menu"
        >
          {isArchived ? (
            <button type="button" role="menuitem" onClick={() => handleAction(onRestore)} disabled={actionBusy}>
              Restore
            </button>
          ) : (
            <button type="button" role="menuitem" onClick={() => handleAction(onArchive)} disabled={actionBusy}>
              Archive
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="chat-thread-action-menu__delete"
            onClick={() => handleAction(onDelete)}
            disabled={actionBusy}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function ChatWindow({
  thread,
  messages,
  user,
  userProfiles,
  supportAdminId,
  supportAdminIds = [],
  isMobile,
  onBack,
  isArchived = false,
  onArchive,
  onRestore,
  onDelete,
  actionBusy = false,
  teamNamesById = {},
  routeContext = {}
}) {
  const supportIds = new Set([
    ...supportAdminIds,
    supportAdminId,
  ].filter(Boolean));
  const isSupportUser = (uid) => Boolean(uid && supportIds.has(uid));

  // Helper to get display name for a user
  const getUserName = (uid) => {
    if (!uid) return 'Unknown User';
    if (isSupportUser(uid)) return 'Support';
    const profile = userProfiles?.[uid];
    if (!profile) return 'Unknown User';
    const { firstName = '', lastName = '' } = profile;
    const full = `${firstName} ${lastName}`.trim();
    return full || 'Unnamed';
  };

  if (!thread) {
    return (
      <div className="messages-chat">
        <div className="chat-placeholder" style={{ color: '#666', margin: 'auto' }}>
          Select a conversation to start chatting.
        </div>
      </div>
    );
  }

  const otherId = thread.participants?.find(uid => uid !== user?.uid) || null;
  const isSupportChat = isSupportUser(otherId);
  const teamName = thread.teamId ? teamNamesById[thread.teamId] || 'Club conversation' : '';

  return (
    <div className="messages-chat">
      {/* Chat header */}
      <div
        className="chat-header"
        style={{
          backgroundColor: isSupportChat ? '#7CB0A1' : '#f5f5f5',
          color: isSupportChat ? '#fff' : '#000',
          display: 'flex',
          alignItems: 'center',
          padding: '10px',
          gap: '10px'
        }}
      >
        {/* Mobile back button */}
        {isMobile && onBack && (
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: 'none',
              color: isSupportChat ? '#fff' : '#000',
              fontSize: '1.2rem',
              cursor: 'pointer',
              padding: '0 5px'
            }}
            aria-label="Back to conversations"
          >
            ←
          </button>
        )}

        {isSupportChat && (
          <span className="support-chat-brand-bubble">
            Member Nest Support
          </span>
        )}

        {!isSupportChat && (
          <div className="chat-header-title">
            <strong>{getUserName(otherId)}</strong>
            {teamName && <span>{teamName}</span>}
          </div>
        )}

        {isSupportChat && <div className="chat-header-title" aria-hidden="true" />}

        <ChatThreadActionMenu
          threadId={thread.id}
          isArchived={isArchived}
          onArchive={onArchive}
          onRestore={onRestore}
          onDelete={onDelete}
          actionBusy={actionBusy}
          isSupportChat={isSupportChat}
        />
      </div>

      {/* Messages */}
      <ChatMessages
        messages={messages.map(msg => ({
          ...msg,
          isSupport: isSupportChat && isSupportUser(msg.senderId)
        }))}
        currentUserId={user?.uid}
      />

      {/* Input */}
      <ChatInput
        threadId={thread.id}
        senderId={user?.uid}
        participants={thread.participants || []}
        userProfiles={userProfiles}
        routeContext={routeContext}
      />
    </div>
  );
}

ChatWindow.propTypes = {
  thread: PropTypes.shape({
    id: PropTypes.string.isRequired,
    participants: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      senderId: PropTypes.string.isRequired,
      content: PropTypes.string
    })
  ).isRequired,
  user: PropTypes.shape({ uid: PropTypes.string.isRequired }).isRequired,
  userProfiles: PropTypes.objectOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string
    })
  ).isRequired,
  supportAdminId: PropTypes.string,
  supportAdminIds: PropTypes.arrayOf(PropTypes.string),
  isMobile: PropTypes.bool,
  onBack: PropTypes.func,
  isArchived: PropTypes.bool,
  onArchive: PropTypes.func,
  onRestore: PropTypes.func,
  onDelete: PropTypes.func,
  actionBusy: PropTypes.bool,
  teamNamesById: PropTypes.objectOf(PropTypes.string),
  routeContext: PropTypes.object
};

ChatThreadActionMenu.propTypes = {
  threadId: PropTypes.string.isRequired,
  isArchived: PropTypes.bool,
  onArchive: PropTypes.func,
  onRestore: PropTypes.func,
  onDelete: PropTypes.func,
  actionBusy: PropTypes.bool,
  isSupportChat: PropTypes.bool
};
